import { ArrowLeft } from "lucide-react";
import { Header } from "@/components/sections/header";
import { Footer } from "@/components/sections/footer";
import { Container } from "@/components/ui/container";
import type { LegalHeading } from "@/lib/legal-markdown";
import type { ReactNode } from "react";

interface LegalLayoutProps {
  title: string;
  statusNote: string | null;
  lastUpdated: string | null;
  headings: LegalHeading[];
  children: ReactNode;
}

/**
 * Shared shell for /privacy and /terms: the same header/footer/design
 * system as the landing page, a readable measure for the legal body copy,
 * and a clear, low-friction way back to the main site.
 */
export function LegalLayout({ title, statusNote, lastUpdated, headings, children }: LegalLayoutProps) {
  return (
    <>
      <Header />
      <main id="main-content" className="py-12 sm:py-16">
        <Container>
          <div className="mx-auto max-w-[70ch]">
            <a
              href="/"
              className="inline-flex items-center gap-1.5 rounded-md text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
            >
              <ArrowLeft className="h-4 w-4" aria-hidden="true" />
              Back to DataProtection.work
            </a>

            <h1 className="mt-6 text-3xl font-extrabold tracking-tight text-foreground sm:text-4xl">
              {title}
            </h1>

            {lastUpdated && (
              <p className="mt-3 text-sm font-medium text-muted-foreground">
                Last updated: {lastUpdated}
              </p>
            )}

            {statusNote && (
              <div
                role="note"
                className="mt-6 rounded-lg border border-border bg-secondary/50 px-4 py-3 text-sm leading-relaxed text-muted-foreground"
              >
                {statusNote}
              </div>
            )}

            {headings.length > 0 && (
              <nav aria-label="Contents" className="mt-8 rounded-lg border border-border bg-card p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Contents
                </p>
                <ul className="mt-2.5 grid gap-1.5 sm:grid-cols-2">
                  {headings.map((heading) => (
                    <li key={heading.id}>
                      <a
                        href={`#${heading.id}`}
                        className="text-sm text-primary underline-offset-2 hover:underline"
                      >
                        {heading.text}
                      </a>
                    </li>
                  ))}
                </ul>
              </nav>
            )}

            <article className="mt-4">{children}</article>

            <div className="mt-12 border-t border-border pt-6">
              <a
                href="/"
                className="inline-flex items-center gap-1.5 rounded-md text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
              >
                <ArrowLeft className="h-4 w-4" aria-hidden="true" />
                Back to DataProtection.work
              </a>
            </div>
          </div>
        </Container>
      </main>
      <Footer />
    </>
  );
}
